import type { BaseUIEvent } from '@base-ui/react';
import { t } from '@lingui/core/macro';
import lodashDebounce from 'lodash/debounce';
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';

import ClearIcon from '@/assets/svg/bx-x.svg?react';
import SearchIcon from '@/assets/svg/search.svg?react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextInput,
} from '@/components/Form/inputs/InputGroup';
import { type TextInputProps } from '@/components/Form/inputs/TextInput';

export type SearchInputProps = Omit<TextInputProps, 'className'> & {
  className?: string;
  allowClear?: boolean;
  debounce?: boolean;
  debounceTime?: number;
  onValueChange?: (value: string) => void;
  onSearch?: (value: string) => void;
};

const SearchInput = ({
  className,
  allowClear = true,
  debounce = true,
  debounceTime = 750,
  defaultValue,
  disabled,
  placeholder = t`Search`,
  size = 'large',
  value,
  onKeyDown,
  onValueChange,
  onSearch = () => {},
  ...rest
}: SearchInputProps) => {
  const initialValue = value !== undefined ? value : defaultValue;
  const [searchValue, setSearchValue] = useState<string | undefined>(
    initialValue !== undefined ? String(initialValue) : undefined,
  );
  const [prevValue, setPrevValue] = useState(value);

  if (value !== prevValue) {
    setPrevValue(value);
    setSearchValue(value !== undefined ? String(value) : undefined);
  }

  const handleSearch = useMemo(
    () => lodashDebounce(onSearch, debounce ? debounceTime : 0),
    [onSearch, debounce, debounceTime],
  );

  const handleValueChange = (newValue: string) => {
    setSearchValue(newValue);
    onValueChange?.(newValue);
    handleSearch(newValue);

    if (newValue === '') {
      handleSearch.flush();
    }
  };

  const handleClear = () => {
    setSearchValue('');
    onValueChange?.('');
    onSearch('');
    handleSearch.flush();
  };

  const handleKeyDown = (event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>) => {
    if (event.key === 'Enter') {
      handleSearch.flush();
    }
    onKeyDown?.(event);
  };

  useEffect(() => {
    return () => {
      handleSearch?.cancel?.();
    };
  }, [handleSearch]);

  const hasValue = (searchValue ?? '').length > 0;
  const showClear = allowClear && hasValue && !disabled;

  return (
    <InputGroup size={size} className={className}>
      <InputGroupTextInput
        {...rest}
        size={size}
        placeholder={placeholder}
        value={searchValue}
        onValueChange={handleValueChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      <InputGroupAddon align="inline-end">
        {showClear ? (
          <InputGroupButton
            size={size === 'large' ? 'icon' : 'icon-small'}
            shape="round"
            aria-label={t`Clear search`}
            onClick={handleClear}
          >
            <ClearIcon data-testid="search-clear" />
          </InputGroupButton>
        ) : (
          <SearchIcon />
        )}
      </InputGroupAddon>
    </InputGroup>
  );
};

SearchInput.displayName = 'SearchInput';

export default SearchInput;
